/**
 * Per-row kebab menu for the FP Monthly COGS Tracker grid.
 *
 * Separate from the month-cell click (which opens the per-month sheet
 * tab) so the row-level affordances stay out of the way of the at-a-
 * glance pivot scan. Two external links (COGS + Product Avg Cost
 * workbooks) and two in-app navigations (inspector exception detail
 * for ``ex-cogs-monthly-incomplete`` and the inspector overview).
 *
 * In-app links use ``component={RouterLink}`` so right-click and
 * ctrl-click open in a new tab (per RULES.md frontend navigation).
 */
import React from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { generateInspectorUrl } from '../../../../../utils/urls'

interface CogsRowActionsMenuProps {
  fpCode: string
  fpId?: number
  cogsSheetUrl: string | null
  pacSheetUrl: string | null
}

const CogsRowActionsMenu: React.FC<CogsRowActionsMenuProps> = ({
  fpCode,
  fpId,
  cogsSheetUrl,
  pacSheetUrl,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const exceptionPath = generateInspectorUrl(fpCode, {
    id: fpId,
    subpath: 'exceptions/accounting/ex-cogs-monthly-incomplete',
  })
  const inspectorPath = generateInspectorUrl(fpCode, { id: fpId })

  return (
    <>
      <Tooltip title="Row actions">
        <IconButton
          size="small"
          aria-label={`Row actions for ${fpCode}`}
          onClick={handleOpen}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {cogsSheetUrl ? (
          <MenuItem
            component="a"
            href={cogsSheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open COGS workbook" />
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open COGS workbook" />
          </MenuItem>
        )}
        {pacSheetUrl ? (
          <MenuItem
            component="a"
            href={pacSheetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open Product Avg Cost workbook" />
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open Product Avg Cost workbook" />
          </MenuItem>
        )}
        <MenuItem
          component={RouterLink}
          to={exceptionPath}
          onClick={handleClose}
        >
          <ListItemIcon>
            <ReportProblemOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View exceptions" />
        </MenuItem>
        <MenuItem
          component={RouterLink}
          to={inspectorPath}
          onClick={handleClose}
        >
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Open inspector" />
        </MenuItem>
      </Menu>
    </>
  )
}

export default CogsRowActionsMenu
